import React, { useEffect, useState } from 'react';
import {
  Card, Form, Input, Select, Switch, InputNumber, Button, Row, Col,
  Statistic, message, Spin, Divider, Alert, Tabs, Table, Popconfirm, Tag, Space, Typography
} from 'antd';
import {
  PlusOutlined, DeleteOutlined, ReloadOutlined, KeyOutlined,
  SettingOutlined, ApiOutlined, BarChartOutlined, WarningOutlined
} from '@ant-design/icons';
import { adminAPI } from '../api/admin';

const { TextArea } = Input;
const { Text } = Typography;

const PROVIDER_LABELS = {
  gemini:      { label: 'Google Gemini', color: '#4285f4' },
  bynara:      { label: 'Bynara Router (router.bynara.id)', color: '#10a37f' },
  bluesminds:  { label: 'Bluesminds', color: '#8b5cf6' },
  agentrouter: { label: 'AgentRouter', color: '#f59e0b' },
};

export default function AiSettingsManager() {
  const [activeTab, setActiveTab] = useState('general');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Tab 1: Chat Settings
  const [form] = Form.useForm();
  const [models, setModels] = useState([]);
  
  // Tab 2: API Keys
  const [providers, setProviders] = useState([]);
  const [newKeys, setNewKeys] = useState({}); // providerName → input value
  const [adding, setAdding] = useState({}); // providerName → bool
  
  // Tab 3: Stats & Errors
  const [stats, setStats] = useState(null);
  const [errors, setErrors] = useState([]);
  const [loadingErrors, setLoadingErrors] = useState(false);

  useEffect(() => {
    loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadAll = async () => {
    setLoading(true);
    try {
      await Promise.all([
        loadChatSettings(),
        loadKeys(),
        loadStatsAndErrors()
      ]);
    } catch (e) {
      message.error('خطا در بارگذاری اطلاعات تنظیمات هوش مصنوعی');
    } finally {
      setLoading(false);
    }
  };

  const loadChatSettings = async () => {
    try {
      const res = await adminAPI.getChatSettings();
      if (res.data.success) {
        const s = res.data.settings;
        setModels(s.availableModels || []);
        form.setFieldsValue(s);
      }
    } catch (e) {
      console.error(e);
    }
  };

  const loadKeys = async () => {
    try {
      const res = await adminAPI.getApiKeys();
      if (res.data.success) {
        setProviders(res.data.providers || []);
      }
    } catch (e) {
      console.error(e);
    }
  };

  const loadStatsAndErrors = async () => {
    setLoadingErrors(true);
    try {
      const [statsRes, errorsRes] = await Promise.all([
        adminAPI.getChatStats().catch(() => null),
        adminAPI.getChatErrors(20).catch(() => null)
      ]);
      if (statsRes?.data?.success) {
        setStats(statsRes.data.stats);
      }
      if (errorsRes?.data?.success) {
        setErrors(errorsRes.data.errors || []);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoadingErrors(false);
    }
  };

  // Save Chat Settings
  const onSaveSettings = async (values) => {
    setSaving(true);
    try {
      await adminAPI.updateChatSettings(values);
      message.success('تنظیمات چت ذخیره شد');
      await loadChatSettings();
    } catch (e) {
      message.error(e.response?.data?.message || 'خطا در ذخیره تنظیمات');
    } finally {
      setSaving(false);
    }
  };

  // Add API Key
  const handleAddKey = async (providerName) => {
    const key = (newKeys[providerName] || '').trim();
    if (!key) return message.warning('کلید API را وارد کنید.');
    setAdding(a => ({ ...a, [providerName]: true }));
    try {
      const res = await adminAPI.addApiKey(providerName, key);
      if (res.data.success) {
        message.success(res.data.message);
        setNewKeys(k => ({ ...k, [providerName]: '' }));
        await loadKeys();
      } else {
        message.error(res.data.message || 'خطا در افزودن کلید');
      }
    } catch (e) {
      message.error(e?.response?.data?.message || 'خطا در اتصال به سرور');
    } finally {
      setAdding(a => ({ ...a, [providerName]: false }));
    }
  };

  // Remove API Key
  const handleRemoveKey = async (providerName, keyIndex) => {
    try {
      const res = await adminAPI.removeApiKey(providerName, keyIndex);
      if (res.data.success) {
        message.success(res.data.message);
        await loadKeys();
      } else {
        message.error(res.data.message || 'خطا در حذف کلید');
      }
    } catch {
      message.error('خطا در حذف کلید');
    }
  };

  if (loading) return <Spin style={{ display: 'block', margin: '80px auto' }} size="large" spinning />;

  const errorColumns = [
    {
      title: 'بخش/سرویس',
      dataIndex: 'context',
      key: 'context',
      width: 120,
      render: (v) => <Tag color="red">{v}</Tag>
    },
    {
      title: 'پیام خطا',
      dataIndex: 'message',
      key: 'message',
      ellipsis: true,
    },
    {
      title: 'تاریخ ثبت',
      dataIndex: 'timestamp',
      key: 'timestamp',
      width: 180,
      render: (v) => <span dir="ltr">{new Date(v).toLocaleString('fa-IR')}</span>
    }
  ];

  return (
    <div>
      <Tabs
        activeKey={activeTab}
        onChange={setActiveTab}
        style={{ marginBottom: 20 }}
        items={[
          {
            key: 'general',
            label: (
              <span>
                <SettingOutlined />
                تنظیمات چت (دامبلدور)
              </span>
            ),
            children: (
              <Card title="تنظیمات دستیار هوش مصنوعی">
                <Form form={form} layout="vertical" onFinish={onSaveSettings}>
                  <Alert
                    type="info"
                    showIcon
                    style={{ marginBottom: 20 }}
                    message="تنظیمات رفتار و مدل هوش مصنوعی"
                    description="در این بخش می‌توانید پرامپت شخصیت‌دهی به پروفسور دامبلدور، مدل پیش‌فرض و پارامپت‌های مربوط به تولید پاسخ و نرخ محدودسازی درخواست‌ها را تغییر دهید."
                  />

                  <Row gutter={16}>
                    <Col xs={24} md={12}>
                      <Form.Item name="enabled" label="فعال بودن چت" valuePropName="checked">
                        <Switch checkedChildren="فعال" unCheckedChildren="غیرفعال" />
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={12}>
                      <Form.Item name="defaultModel" label="مدل پیش‌فرض چت" rules={[{ required: true }]}>
                        <Select options={models.map((m) => ({ value: m, label: m }))} />
                      </Form.Item>
                    </Col>
                  </Row>

                  <Form.Item
                    name="proxyUrl"
                    label="آدرس پروکسی ارتباط با هوش مصنوعی (اختیاری)"
                    tooltip="برای اتصال به سرورهایی مثل Gemini، آدرس Socks5 یا HTTP را وارد کنید. اگر خالی باشد، از متغیرهای محیطی (.env) خوانده می‌شود. مثال: socks5://127.0.0.1:1080 یا tg://socks?..."
                  >
                    <Input placeholder="مثال: socks5://127.0.0.1:1080 یا http://127.0.0.1:2080" dir="ltr" />
                  </Form.Item>

                  <Form.Item
                    name="systemPrompt"
                    label="پرامپت سیستم (شخصیت و قوانین پاسخ‌دهی)"
                    rules={[{ required: true, message: 'پرامپت سیستم الزامی است' }]}
                  >
                    <TextArea rows={8} />
                  </Form.Item>

                  <Row gutter={16}>
                    <Col xs={24} md={8}>
                      <Form.Item name="temperature" label="دما (Temperature)">
                        <InputNumber min={0} max={2} step={0.1} style={{ width: '100%' }} />
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={8}>
                      <Form.Item name="maxTokens" label="حداکثر طول پاسخ (Tokens)">
                        <InputNumber min={64} max={8000} step={64} style={{ width: '100%' }} />
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={8}>
                      <Form.Item name="guestMessageLimit" label="سقف پیام کاربر مهمان">
                        <InputNumber min={0} max={1000} style={{ width: '100%' }} />
                      </Form.Item>
                    </Col>
                  </Row>

                  <Divider>محدودسازی نرخ درخواست</Divider>
                  <Row gutter={16}>
                    <Col xs={24} md={8}>
                      <Form.Item name="rateLimitMax" label="حداکثر درخواست در هر بازه">
                        <InputNumber min={1} max={1000} style={{ width: '100%' }} />
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={8}>
                      <Form.Item name="rateLimitWindowMs" label="طول بازه (میلی‌ثانیه)">
                        <InputNumber min={1000} step={1000} style={{ width: '100%' }} />
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={8}>
                      <Form.Item name="cacheEnabled" label="کش پاسخ‌ها" valuePropName="checked">
                        <Switch checkedChildren="فعال" unCheckedChildren="غیرفعال" />
                      </Form.Item>
                    </Col>
                  </Row>

                  <Button type="primary" htmlType="submit" loading={saving}>
                    ذخیره تنظیمات
                  </Button>
                </Form>
              </Card>
            )
          },
          {
            key: 'keys',
            label: (
              <span>
                <ApiOutlined />
                مدیریت کلیدهای API
              </span>
            ),
            children: (
              <div>
                <Alert
                  type="info"
                  showIcon
                  style={{ marginBottom: 20 }}
                  message="سیستم سوییچ خودکار کلیدها و ارائه‌دهنده‌های API"
                  description="در این بخش می‌توانید برای هر ارائه‌دهنده چندین کلید API ثبت کنید. هنگامی که یک کلید به محدودیت سهمیه (Quota limit) یا نرخ درخواست (Rate limit) برسد، سیستم به‌طور خودکار روی کلید بعدی سوییچ می‌کند. همچنین در صورت بروز خطای سراسری در یک ارائه‌دهنده، کل درخواست به ارائه‌دهنده بعدی فرستاده می‌شود."
                />

                <Button
                  icon={<ReloadOutlined />}
                  onClick={loadKeys}
                  style={{ marginBottom: 16 }}
                >
                  بارگذاری مجدد کلیدها
                </Button>

                {providers.map((provider) => {
                  const meta = PROVIDER_LABELS[provider.name] || { label: provider.name, color: '#666' };
                  const columns = [
                    {
                      title: '#',
                      dataIndex: 'index',
                      width: 48,
                      render: (v) => <Text type="secondary">{v + 1}</Text>
                    },
                    {
                      title: 'کلید (ماسک‌شده)',
                      dataIndex: 'masked',
                      render: (v) => (
                        <Text code style={{ fontSize: 13, letterSpacing: '0.05em' }}>
                          <KeyOutlined style={{ marginLeft: 6, color: '#888' }} />{v}
                        </Text>
                      )
                    },
                    {
                      title: 'حذف',
                      width: 80,
                      align: 'center',
                      render: (_, row) => (
                        <Popconfirm
                          title="این کلید حذف شود؟"
                          onConfirm={() => handleRemoveKey(provider.name, row.index)}
                          okText="بله"
                          cancelText="خیر"
                        >
                          <Button danger size="small" icon={<DeleteOutlined />} disabled={provider.keyCount <= 1} />
                        </Popconfirm>
                      )
                    }
                  ];

                  return (
                    <Card
                      key={provider.name}
                      style={{ marginBottom: 16, borderTop: `3px solid ${meta.color}` }}
                      title={
                        <Space>
                          <Tag color={meta.color} style={{ fontFamily: 'monospace', fontSize: 12 }}>
                            {provider.name}
                          </Tag>
                          <span>{meta.label}</span>
                          <Tag>{provider.keyCount} کلید</Tag>
                        </Space>
                      }
                      extra={
                        <Text type="secondary" style={{ fontSize: 12 }}>
                          {provider.apiBaseUrl}
                        </Text>
                      }
                    >
                      <Table
                        dataSource={provider.keys}
                        columns={columns}
                        rowKey="index"
                        size="small"
                        pagination={false}
                        style={{ marginBottom: 16 }}
                        locale={{ emptyText: 'هیچ کلیدی ثبت نشده' }}
                      />

                      <Space.Compact style={{ width: '100%' }}>
                        <Input
                          placeholder="کلید API جدید را اینجا وارد کنید..."
                          value={newKeys[provider.name] || ''}
                          onChange={(e) => setNewKeys(k => ({ ...k, [provider.name]: e.target.value }))}
                          onPressEnter={() => handleAddKey(provider.name)}
                          type="password"
                          style={{ fontFamily: 'monospace', fontSize: 13 }}
                        />
                        <Button
                          type="primary"
                          icon={<PlusOutlined />}
                          loading={adding[provider.name]}
                          onClick={() => handleAddKey(provider.name)}
                        >
                          افزودن کلید
                        </Button>
                      </Space.Compact>
                    </Card>
                  );
                })}
              </div>
            )
          },
          {
            key: 'stats',
            label: (
              <span>
                <BarChartOutlined />
                آمار و خطاها
              </span>
            ),
            children: (
              <div>
                {stats && (
                  <Row gutter={16} style={{ marginBottom: 24 }}>
                    <Col xs={12} lg={6}><Card><Statistic title="گفتگوها" value={stats.conversations} /></Card></Col>
                    <Col xs={12} lg={6}><Card><Statistic title="کل پیام‌ها" value={stats.totalMessages} /></Card></Col>
                    <Col xs={12} lg={6}><Card><Statistic title="توکن مصرفی" value={stats.totalTokens} /></Card></Col>
                    <Col xs={12} lg={6}><Card><Statistic title="خطاها" value={stats.errors} valueStyle={{ color: '#cf1322' }} prefix={<WarningOutlined />} /></Card></Col>
                  </Row>
                )}

                <Card
                  title="آخرین خطاهای ثبت‌شده هوش مصنوعی"
                  extra={
                    <Button icon={<ReloadOutlined />} onClick={loadStatsAndErrors} loading={loadingErrors}>
                      به‌روزرسانی
                    </Button>
                  }
                >
                  <Table
                    dataSource={errors}
                    columns={errorColumns}
                    rowKey="_id"
                    loading={loadingErrors}
                    size="small"
                    locale={{ emptyText: 'هیچ خطایی ثبت نشده است.' }}
                    expandable={{
                      expandedRowRender: (record) => (
                        <div style={{ padding: '8px 16px', background: '#fafafa', borderRadius: 4 }}>
                          <Text strong>Stack Trace / جزئیات خطا:</Text>
                          <pre style={{ marginTop: 8, overflowX: 'auto', fontSize: 11, color: '#ff4d4f' }}>
                            {record.stack || JSON.stringify(record.meta, null, 2) || 'جزئیات بیشتری در دسترس نیست'}
                          </pre>
                        </div>
                      )
                    }}
                  />
                </Card>
              </div>
            )
          }
        ]}
      />
    </div>
  );
}
